import React from "react";
import { Dropdown } from "react-bootstrap";
import { useLogoutMutation } from "common/services";
import { useOktaSession } from "hooks";
import { markSilentAttempted } from "features/Login";
import styles from "./UserMenu.module.scss";

type ToggleProps = {
  children?: React.ReactNode;
  onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
};

// react-bootstrap's <Dropdown.Toggle as={...}> needs a ref-forwarding element to anchor the menu.
const PillToggle = React.forwardRef<HTMLButtonElement, ToggleProps>(
  ({ children, onClick }, ref) => (
    <button
      type="button"
      ref={ref}
      className={styles.pill}
      onClick={(event) => {
        event.preventDefault();
        onClick?.(event);
      }}
    >
      {children}
    </button>
  ),
);
PillToggle.displayName = "UserMenuToggle";

// Inline SVGs (currentColor) so the icons render reliably without a webfont icon pack.
const UserIcon: React.FC = () => (
  <svg
    className={styles.userIcon}
    width="17"
    height="17"
    viewBox="0 0 24 24"
    fill="currentColor"
    aria-hidden="true"
  >
    <path d="M12 12a4 4 0 1 0 0-8 4 4 0 0 0 0 8zm0 2c-3.31 0-8 1.66-8 5v1h16v-1c0-3.34-4.69-5-8-5z" />
  </svg>
);

const ChevronIcon: React.FC = () => (
  <svg
    className={styles.caret}
    width="11"
    height="11"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

const SignOutIcon: React.FC = () => (
  <svg
    className={styles.signOutIcon}
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
    <polyline points="16 17 21 12 16 7" />
    <line x1="21" y1="12" x2="9" y2="12" />
  </svg>
);

/**
 * Top-right account menu. Shows the current user (from the QQT BFF cookie session resolved via
 * `/auth/me`) as a pill and offers a Sign out action that clears the server-side session.
 */
export const UserMenu: React.FC = () => {
  const { isAuthenticated, me } = useOktaSession();
  const [logout, { isLoading }] = useLogoutMutation();

  // Only render when a live QQT (Okta/BFF) cookie session exists.
  if (!isAuthenticated || !me) {
    return null;
  }

  const displayName = me.displayName || me.email || "Account";
  const firstName = displayName.trim().split(/\s+/)[0];

  const onSignOut = async () => {
    let logoutUrl: string | null | undefined;
    try {
      // Clears the server-side session/cookie and returns Okta's RP-initiated logout URL.
      ({ logoutUrl } = await logout().unwrap());
    } catch {
      // Best-effort: even if the server call fails, still send the user to the login screen.
    }
    // Prevent the login page from immediately re-authenticating through the shared Okta session.
    markSilentAttempted();
    // When Okta backed the session, navigate to its end-session endpoint so the upstream Okta
    // session is torn down too; Okta then redirects back to the app. Otherwise just go home.
    window.location.href = logoutUrl || "/";
  };

  return (
    <Dropdown align="end" className={styles.menu}>
      <Dropdown.Toggle as={PillToggle}>
        <UserIcon />
        <span className={styles.name}>{firstName}</span>
        <ChevronIcon />
      </Dropdown.Toggle>
      <Dropdown.Menu className={styles.dropdown}>
        <div className={styles.identity}>
          <div className={styles.identityName}>{displayName}</div>
          {me.email && <div className={styles.identityEmail}>{me.email}</div>}
          {me.provider && (
            <div className={styles.identityProvider}>Signed in via {me.provider}</div>
          )}
        </div>
        <Dropdown.Divider />
        <Dropdown.Item onClick={onSignOut} disabled={isLoading} className={styles.signOutItem}>
          <SignOutIcon />
          {isLoading ? "Signing out…" : "Sign out"}
        </Dropdown.Item>
      </Dropdown.Menu>
    </Dropdown>
  );
};
